import m from "moment";

/** Parada só conta se durar pelo menos 5 min (retroativo ao início da parada). */
export const STOPPED_MIN_DURATION_MS = 5 * 60 * 1000;

/** Sem nova coordenada por 5 min → candidato a parada (telemetria ~5 min). */
export const NO_SIGNAL_GAP_MS = 5 * 60 * 1000;

export const MIN_MOVING_SPEED_KMH = 1;

/** @deprecated use STOPPED_MIN_DURATION_MS */
export const COORD_IDLE_GAP_MS = NO_SIGNAL_GAP_MS;

/** @deprecated use NO_SIGNAL_GAP_MS */
export const MOTION_MAX_GAP_MS = NO_SIGNAL_GAP_MS;

function isStoppedSample(speed) {
  return (Number(speed) || 0) <= MIN_MOVING_SPEED_KMH;
}

function isStoppedInterval(prev, curr, gapMs) {
  if (gapMs >= NO_SIGNAL_GAP_MS) {
    return true;
  }
  return isStoppedSample(prev.velocidade) && isStoppedSample(curr.velocidade);
}

/**
 * Calcula início/fim e tempos com base nos pontos visíveis no gráfico.
 * Regra: paradas < 5 min contam como movimento; paradas ≥ 5 min contam como
 * parado desde o instante em que a máquina parou (retroativo).
 * @param {Array<{time: number, velocidade: number}>} points
 */
export function calculatePeriodMotionStats(points) {
  if (!points || points.length === 0) {
    return {
      startTimeMs: null,
      endTimeMs: null,
      inMotionTimeMs: 0,
      idleTimeMs: 0,
    };
  }

  if (points.length === 1) {
    return {
      startTimeMs: points[0].time,
      endTimeMs: points[0].time,
      inMotionTimeMs: 0,
      idleTimeMs: 0,
    };
  }

  const intervals = [];
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const gapMs = curr.time - prev.time;
    intervals.push({
      startMs: prev.time,
      endMs: curr.time,
      durationMs: gapMs,
      stopped: isStoppedInterval(prev, curr, gapMs),
    });
  }

  let inMotionTimeMs = 0;
  let idleTimeMs = 0;
  let episodeStartMs = null;
  let episodeEndMs = null;

  const flushStopEpisode = () => {
    if (episodeStartMs == null || episodeEndMs == null) {
      return;
    }
    const durationMs = episodeEndMs - episodeStartMs;
    if (durationMs >= STOPPED_MIN_DURATION_MS) {
      idleTimeMs += durationMs;
    } else {
      inMotionTimeMs += durationMs;
    }
    episodeStartMs = null;
    episodeEndMs = null;
  };

  intervals.forEach((interval) => {
    if (interval.stopped) {
      if (episodeStartMs == null) {
        episodeStartMs = interval.startMs;
      }
      episodeEndMs = interval.endMs;
      return;
    }

    flushStopEpisode();
    inMotionTimeMs += interval.durationMs;
  });

  flushStopEpisode();

  return {
    startTimeMs: points[0].time,
    endTimeMs: points[points.length - 1].time,
    inMotionTimeMs,
    idleTimeMs,
  };
}

export function formatPeriodClockDuration(totalMs) {
  const totalSeconds = Math.max(0, Math.floor(totalMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}

export function formatPeriodTimestamp(timeMs) {
  if (timeMs == null) return "—";
  return m(timeMs).format("DD/MM/YYYY HH:mm:ss");
}

function toRadians(degree) {
  return (degree * Math.PI) / 180;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Distância e velocidades a partir dos pontos do mapa (lat/lng + speed).
 * @param {Array<{lat: number, lng: number, speed?: string}>} pathPoints
 */
export function calculatePathDistanceStats(pathPoints) {
  if (!pathPoints || pathPoints.length === 0) {
    return {
      distanceKm: 0,
      avgSpeedKmh: 0,
      maxSpeedKmh: 0,
      pointCount: 0,
    };
  }

  let distanceKm = 0;
  for (let i = 1; i < pathPoints.length; i += 1) {
    const prev = pathPoints[i - 1];
    const curr = pathPoints[i];
    distanceKm += haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);
  }

  const speeds = pathPoints
    .map((point) => Number(String(point.speed || "").split(" km/h")[0]) || 0)
    .filter((value) => !Number.isNaN(value));

  const avgSpeedKmh = speeds.length
    ? speeds.reduce((sum, value) => sum + value, 0) / speeds.length
    : 0;
  const maxSpeedKmh = speeds.length ? Math.max(...speeds) : 0;

  return {
    distanceKm: Math.round(distanceKm * 100) / 100,
    avgSpeedKmh: Math.round(avgSpeedKmh * 10) / 10,
    maxSpeedKmh: Math.round(maxSpeedKmh * 10) / 10,
    pointCount: pathPoints.length,
  };
}

/** Distância só em trechos com movimento (velocidade acima do limiar). */
export function calculateMovingPathDistanceStats(pathPoints) {
  if (!pathPoints || pathPoints.length < 2) {
    return {
      distanceKm: 0,
      pointCount: pathPoints ? pathPoints.length : 0,
    };
  }

  let distanceKm = 0;
  for (let i = 1; i < pathPoints.length; i += 1) {
    const prev = pathPoints[i - 1];
    const curr = pathPoints[i];
    const prevSpeed = Number(String(prev.speed || "").split(" km/h")[0]) || 0;
    const currSpeed = Number(String(curr.speed || "").split(" km/h")[0]) || 0;
    if (prevSpeed <= MIN_MOVING_SPEED_KMH && currSpeed <= MIN_MOVING_SPEED_KMH) {
      continue;
    }
    distanceKm += haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);
  }

  return {
    distanceKm: Math.round(distanceKm * 100) / 100,
    pointCount: pathPoints.length,
  };
}

/**
 * Tempo parado com telemetria ativa (pontos chegando, velocidade ≈ 0).
 * Não inclui intervalos longos sem sinal GPS.
 */
export function calculateEngineOnStoppedTimeMs(points) {
  if (!points || points.length < 2) return 0;

  let totalMs = 0;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const gapMs = curr.time - prev.time;
    if (gapMs >= NO_SIGNAL_GAP_MS) continue;
    if (isStoppedSample(prev.velocidade) && isStoppedSample(curr.velocidade)) {
      totalMs += gapMs;
    }
  }
  return totalMs;
}

/** Área estimada (ha) = distância (m) × largura de trabalho (m) / 10.000 */
export function estimateWorkedAreaHa(distanceKm, workingWidthM = 12) {
  if (!distanceKm || !workingWidthM) return 0;
  return Math.round(((distanceKm * 1000 * workingWidthM) / 10000) * 100) / 100;
}
